import { useMemo, useState } from "react";
import { BarChart3, Check, Copy, FileSpreadsheet, ThumbsDown, ThumbsUp } from "lucide-react";
import {
  ChatbotChartHint,
  downloadBlob,
  exportChatbotResponse,
  rateChatbotResponse,
} from "../../services/chatbot.service";
import { parseFirstMarkdownTable } from "./markdownTable";
import ChatbotChart from "./ChatbotChart";

interface ResponseActionsProps {
  /** The assistant reply markdown this action bar belongs to. */
  markdown: string;
  /** Meaningful report name (used for the Excel export file name). */
  reportName: string;
  /** Optional chart directive emitted by the model. */
  chart?: ChatbotChartHint | null;
  /** Id of the AiInput log row this answer was persisted as; undefined when logging failed/skipped. */
  aiInputId?: number | null;
}

type Rating = "up" | "down" | null;

const ResponseActions = ({ markdown, reportName, chart, aiInputId }: ResponseActionsProps): JSX.Element => {
  const table = useMemo(() => parseFirstMarkdownTable(markdown), [markdown]);
  const hasTable = !!table;

  const [copied, setCopied] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [rating, setRating] = useState<Rating>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setFailed(true);
      window.setTimeout(() => setFailed(false), 2000);
    }
  };

  const runExport = async () => {
    if (exporting || !table) return;
    setExporting(true);
    setFailed(false);
    try {
      const blob = await exportChatbotResponse(reportName, markdown, table);
      const safe = (reportName || "Chatbot_Report").replace(/[^\w\-]+/g, "_").slice(0, 80);
      downloadBlob(blob, `${safe}.xlsx`);
    } catch {
      setFailed(true);
      window.setTimeout(() => setFailed(false), 2500);
    } finally {
      setExporting(false);
    }
  };

  const rate = async (next: "up" | "down") => {
    if (!aiInputId || rating === next) return;
    setRating(next);
    try {
      await rateChatbotResponse(aiInputId, next === "up" ? 1 : 2);
    } catch {
      // Best-effort — a failed rating shouldn't disrupt the chat experience or surface an error.
    }
  };

  return (
    <div className="chatbot-actions-wrap">
      <div className="chatbot-actions">
        <button className="chatbot-action" onClick={copy} title="Copy response" aria-label="Copy response">
          {copied ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>

        {hasTable && (
          <button
            className={`chatbot-action${showChart ? " active" : ""}`}
            onClick={() => setShowChart((s) => !s)}
            title="Toggle chart"
            aria-label="Toggle chart"
            aria-pressed={showChart}
          >
            <BarChart3 size={14} />
            <span>Chart</span>
          </button>
        )}

        {hasTable && (
          <button
            className="chatbot-action"
            onClick={() => void runExport()}
            disabled={exporting}
            title="Export to Excel"
            aria-label="Export to Excel"
          >
            <FileSpreadsheet size={14} />
            <span>{exporting ? "…" : "Excel"}</span>
          </button>
        )}

        {aiInputId != null && (
          <div className="chatbot-rating" role="group" aria-label="Rate this answer">
            <button
              className={`chatbot-action chatbot-rating-btn${rating === "up" ? " active" : ""}`}
              onClick={() => void rate("up")}
              title="Helpful"
              aria-label="Thumbs up"
              aria-pressed={rating === "up"}
            >
              <ThumbsUp size={14} />
            </button>
            <button
              className={`chatbot-action chatbot-rating-btn${rating === "down" ? " active" : ""}`}
              onClick={() => void rate("down")}
              title="Not helpful"
              aria-label="Thumbs down"
              aria-pressed={rating === "down"}
            >
              <ThumbsDown size={14} />
            </button>
          </div>
        )}
      </div>

      {failed && <div className="chatbot-action-error sub_text">Couldn't complete that — please try again.</div>}

      {showChart && table && <ChatbotChart table={table} hint={chart} />}
    </div>
  );
};

export default ResponseActions;
